import {
  Box,
  Container,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  useDisclosure,
} from '@chakra-ui/react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { Bleed, Button, Card, Checkbox, Option, Select, TextField } from '@main/complykit';
import { useDeleteOrganizationInvitationMutation } from '@main/graphql/mutations/DeleteOrganizationInvitation.generated';
import { useInviteUserMutation } from '@main/graphql/mutations/InviteUser.generated';
import {
  GetOrganizationInvitationsQuery,
  useGetOrganizationInvitationsQuery,
} from '@main/graphql/queries/GetOrganizationInvitations.generated';
import { isGraphqlError } from '@main/shared/utils';
import {
  actionHelper,
  createColumnHelper,
  errorToast,
  successToast,
  Table,
  TableEmptyState,
  useAlertDialog,
} from '@main/ui';
import { useUserId } from '@nhost/react';
import { useCallback, useMemo } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod/v4';

import { getRoleTagConfig } from './constants';
import { useGetOrgCustomRolesQuery } from './get-user-roles.admin.generated';

const roleSchema = z.object(
  {
    label: z.string(),
    value: z.string(),
  },
  { error: 'Select a role.' },
);

const inviteSchema = z.object({
  email: z.email('Enter a valid e-mail.'),
  role: roleSchema,
  overrideSSOEnforcement: z.boolean(),
});

type InviteSchema = z.infer<typeof inviteSchema>;

const tableId = 'invitations';

export const InvitationsTab = ({ orgId }: { orgId: string }) => {
  const userId = useUserId();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { control, handleSubmit, reset, setError, setValue } = useForm<InviteSchema>({
    resolver: zodResolver(inviteSchema),
    defaultValues: {
      email: '',
      overrideSSOEnforcement: false,
    },
  });

  const [sendInvite, { isLoading: isSending }] = useInviteUserMutation();
  const { data, refetch: refetchInvitations } = useGetOrganizationInvitationsQuery({
    orgId,
  });
  const { data: rolesData } = useGetOrgCustomRolesQuery({
    orgId,
  });

  const invitations = data?.invitations || [];

  const columns = useInvitationsTableColumns(orgId);
  const closeModal = () => {
    setValue('overrideSSOEnforcement', false);
    onClose();
  };

  const handler: SubmitHandler<InviteSchema> = async (data) => {
    try {
      const result = await sendInvite({
        object: {
          organizationId: orgId,
          email: data.email,
          invitingUserId: userId as string,
          roleId: data.role.value,
          ...(data.overrideSSOEnforcement ? { overrideSSOEnforcement: true } : {}),
        },
      }).unwrap();

      refetchInvitations();
      closeModal();
      reset();
      successToast(result.invite_user?.message || 'Invitation sent');
    } catch (error) {
      if (isGraphqlError(error)) {
        switch (error[0]?.extensions.code) {
          case 'USER_ALREADY_INVITED':
            errorToast('User with this email already invited.');
            return;
          case 'USER_ALREADY_EXISTS':
            errorToast('User with this email already exists');
            return;
          case 'USER_EMAIL_SSO_ENFORCEMENT':
            setError('email', {
              message:
                'Users with this domain use SSO and must normally be provisioned through the identity provider. Select Override SSO enforcement to send an invitation instead',
            });
            return;
          default:
            errorToast('Something went wrong, please try again later.');
            break;
        }
      } else {
        errorToast('Backend may be down, please check and try again.');
      }
      // eslint-disable-next-line no-console
      console.error(error);
    }

    return;
  };

  const roleOptions = useMemo(() => {
    const systemRoles =
      rolesData?.roles
        .filter((role) => role.system_role)
        .map((role) => ({ value: role.id, label: role.system_role ?? role.name })) ?? [];

    const customRoles =
      rolesData?.roles
        .filter((role) => !role.system_role)
        .map((role) => ({ value: role.id, label: role.name })) ?? [];

    return [...systemRoles, ...customRoles];
  }, [rolesData?.roles]);

  const tableItemName = useMemo(() => {
    return {
      singular: 'invitation',
      plural: 'invitations',
    };
  }, []);

  return (
    <Stack spacing={8}>
      <Card>
        <Bleed>
          <Table
            id={tableId}
            data={invitations}
            columns={columns}
            itemName={tableItemName}
            renderEmptyState={(props) => (
              <TableEmptyState
                {...props}
                subHeading="Click the button below to add a new invitation."
              />
            )}
          />
        </Bleed>
      </Card>
      <Box alignSelf="end">
        <Button iconStart={PlusIcon} onPress={onOpen}>
          Invite new users
        </Button>
      </Box>
      <Modal
        size="xl"
        isCentered
        autoFocus={false}
        isOpen={isOpen}
        onClose={closeModal}
        motionPreset="slideInBottom"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="lg">Invite new users</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Container as="form" onSubmit={handleSubmit(handler)} px={0} noValidate>
              <Stack spacing={4}>
                <Controller
                  control={control}
                  name="email"
                  render={({ field, fieldState }) => (
                    <TextField
                      ref={field.ref}
                      type="email"
                      label="E-mail"
                      name={field.name}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      placeholder="Enter an e-mail"
                      error={fieldState.error?.message}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="role"
                  render={({ field, fieldState }) => (
                    <Select
                      ref={field.ref}
                      label="Role"
                      name={field.name}
                      value={field.value?.value ?? null}
                      onChange={(id) => {
                        const option = roleOptions.find((role) => role.value === id);
                        if (option) field.onChange(option);
                      }}
                      onBlur={field.onBlur}
                      isDisabled={field.disabled}
                      error={fieldState.error?.message}
                      placeholder="Select user role"
                      items={roleOptions}
                      emptyMessage="No options"
                    >
                      {(option) => <Option id={option.value}>{option.label}</Option>}
                    </Select>
                  )}
                />
                <Controller
                  control={control}
                  name="overrideSSOEnforcement"
                  render={({ field }) => (
                    <Checkbox
                      name={field.name}
                      isSelected={field.value}
                      onChange={field.onChange}
                      description="Allows the user to sign up with email and password instead of being provisioned through the identity provider."
                    >
                      Override SSO enforcement for this invitation
                    </Checkbox>
                  )}
                />
              </Stack>
              <ModalFooter px={0} gap={2}>
                <Button variant="soft" onPress={closeModal}>
                  Cancel
                </Button>
                <Button type="submit" isPending={isSending}>
                  Invite
                </Button>
              </ModalFooter>
            </Container>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Stack>
  );
};

type Invitation = GetOrganizationInvitationsQuery['invitations'][number];

function useInvitationsTableColumns(orgId: string) {
  const { openDialog } = useAlertDialog();
  const { refetch: refetchInvitations } = useGetOrganizationInvitationsQuery({
    orgId,
  });
  const [deleteInvitation] = useDeleteOrganizationInvitationMutation();

  const deleteHandler = useCallback(
    async (invitationId: string) => {
      try {
        await deleteInvitation({
          id: invitationId,
        }).unwrap();

        await refetchInvitations().unwrap();
        successToast('Successfully deleted invitation');
      } catch {
        errorToast('Failed to delete invitation');
      }
    },
    [deleteInvitation, refetchInvitations],
  );

  return useMemo(() => {
    const columnHelper = createColumnHelper<Invitation>();
    return [
      columnHelper.columns.status({
        id: 'status',
        header: 'Status',
        accessorFn: () => ({
          value: 'Pending',
          colorScheme: 'gray',
        }),
        size: 120,
      }),
      columnHelper.columns.text({
        id: 'email',
        header: 'Email',
        accessorFn: ({ email }) => email,
        enableGlobalFilter: true,
      }),
      columnHelper.columns.tag({
        id: 'role',
        header: 'Role',
        accessorFn: ({ role }) => getRoleTagConfig(role),
        size: 110,
      }),
      columnHelper.columns.actions({
        menuActions: ({ row }) => [
          actionHelper.menuActionItem({
            icon: TrashIcon,
            label: 'Delete',
            onClick: () =>
              openDialog({
                dialogHeader: 'Delete invitation',
                dialogContent: 'Are you sure to delete this invitation?',
                confirmAction: {
                  children: 'Delete',
                  onClick: () => deleteHandler(row.original.id),
                },
              }),
          }),
        ],
      }),
    ];
  }, [deleteHandler, openDialog]);
}
